/** Shared Guest Profile presentation for reservation customer editors. */
import React, { useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import CustomerForm from "../Components/CustomerForm";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { FiSave, FiTrash2, FiUserPlus, FiX } from "react-icons/fi";
import "./BookerCustomerCreatePopup.css";
import { useGuestProfileLanguage, formatGuestProfileFeedback } from "./guestProfileI18n";

function getCustomerSelectStyles(isLightTheme) {
  const palette = isLightTheme
    ? {
        surface: "#ffffff",
        surfaceSoft: "#f7fafc",
        text: "#26354f",
        muted: "#5b6680",
        border: "#7c90a7",
        selected: "#223a7a",
        focus: "#3db6e9",
        hover: "#eaf7fc",
      }
    : {
        surface: "#172343",
        surfaceSoft: "#121f3d",
        text: "#e4edf8",
        muted: "#aebbd0",
        border: "rgba(61, 182, 233, 0.62)",
        selected: "#223a7a",
        focus: "#3db6e9",
        hover: "#1b3154",
      };

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 9,
      borderColor: state.isFocused ? palette.focus : palette.border,
      background: state.isDisabled ? palette.surfaceSoft : palette.surface,
      boxShadow: state.isFocused ? "0 0 0 3px rgba(61, 182, 233, 0.22)" : "none",
      fontSize: "0.75rem",
      opacity: 1,
      ":hover": { borderColor: palette.focus },
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 9px" }),
    input: (base) => ({ ...base, color: palette.text }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? palette.muted : palette.text,
    }),
    placeholder: (base) => ({ ...base, color: palette.muted }),
    dropdownIndicator: (base) => ({ ...base, color: palette.focus }),
    clearIndicator: (base) => ({ ...base, color: palette.muted }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: palette.border }),
    menuPortal: (base) => ({ ...base, zIndex: 100100 }),
    menu: (base) => ({
      ...base,
      overflow: "hidden",
      border: `1px solid ${palette.border}`,
      borderRadius: 10,
      background: palette.surface,
      boxShadow: isLightTheme
        ? "0 18px 38px rgba(34, 58, 122, 0.16)"
        : "0 20px 44px rgba(2, 8, 28, 0.56)",
    }),
    menuList: (base) => ({ ...base, padding: 4 }),
    option: (base, state) => ({
      ...base,
      cursor: "pointer",
      borderRadius: 7,
      color: state.isSelected ? "#ffffff" : palette.text,
      background: state.isSelected ? palette.selected : state.isFocused ? palette.hover : palette.surface,
      fontSize: "0.75rem",
    }),
  };
}

const GuestProfileDialog = ({
  title,
  subtitle,
  contextLabel,
  createLabel,
  removeLabel,
  mode = "create",
  formData,
  handleChange,
  handleSelectChange,
  requiredFields = [],
  onClose,
  onSubmit,
  onRemove,
  saving = false,
  removing = false,
  feedback = "",
  children,
  beforeFields,
  eyebrow = "Guest Profile",
  contextHint = "Required fields are marked with an asterisk.",
  footHint = "The profile will be assigned immediately after saving.",
  submitDisabled = false,
}) => {
  const { themeId } = usePmsTheme();
  const { languageId, t } = useGuestProfileLanguage();
  const isLightTheme = themeId === "light";
  const dialogRef = useRef(null);
  const busyRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const subtitleId = `${dialogId}-subtitle`;
  const customerSelectStyles = useMemo(
    () => getCustomerSelectStyles(isLightTheme),
    [isLightTheme]
  );

  busyRef.current = saving || removing;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const focusableSelector =
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"]):not(:disabled)';
    const initialFocus = dialog?.querySelector('input[name="first_name"], input[name="name"], input[type="search"]') || dialog?.querySelector(focusableSelector);
    if (initialFocus instanceof HTMLElement) initialFocus.focus();
    else dialog?.focus();

    const handleEscapeKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented || busyRef.current) return;
      // Read the menu state before react-select consumes Escape and closes it.
      if (dialog?.querySelector('[role="combobox"][aria-expanded="true"]')) return;
      event.preventDefault();
      onCloseRef.current?.();
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Tab" || !dialog) return;

      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleEscapeKeyDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleEscapeKeyDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (typeof document === "undefined") return null;

  const closePopup = () => {
    if (!busyRef.current) onClose?.();
  };

  return createPortal(
    <div
      className={`booker-profile-overlay${isLightTheme ? " booker-profile-overlay--light" : " booker-profile-overlay--dark"}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closePopup();
      }}
    >
      <form
        ref={dialogRef}
        className="booker-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitleId}
        tabIndex={-1}
        onSubmit={onSubmit}
      >
        <header className="booker-profile-head">
          <div className="booker-profile-heading">
            <div className="booker-profile-icon" aria-hidden="true">
              <FiUserPlus />
            </div>
            <div>
              <p className="booker-profile-eyebrow">{t(eyebrow)}</p>
              <h2 id={titleId} className="booker-profile-title">
                {t(title)}
              </h2>
              <p id={subtitleId} className="booker-profile-subtitle">
                {t(subtitle)}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="booker-profile-close"
            onClick={closePopup}
            disabled={saving || removing}
            aria-label={t("Close guest profile")}
          >
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div className="booker-profile-body">
          <div className="booker-profile-context">
            <div>
              <strong>{t(contextLabel)}</strong>
              <span>{t(contextHint)}</span>
            </div>
            <span className="booker-profile-contextBadge">
              {t("{count} required", { count: requiredFields.length })}
            </span>
          </div>

          {feedback ? (
            <div className="booker-profile-feedback" role="alert">
              {formatGuestProfileFeedback(feedback, languageId)}
            </div>
          ) : null}

          <fieldset className="booker-profile-fieldset" disabled={saving || removing}>
            {beforeFields}
            {children !== undefined ? children : <CustomerForm
              formData={formData}
              handleChange={handleChange}
              handleSelectChange={handleSelectChange}
              requiredFields={requiredFields}
              appearance="pilcore"
              languageId={languageId}
              customerSelectStyles={customerSelectStyles}
              selectMenuPortalTarget={typeof document !== "undefined" ? document.body : null}
              disabled={saving || removing}
            />}
          </fieldset>
        </div>

        <footer className="booker-profile-foot">
          {mode === "edit" && onRemove ? (
            <button
              type="button"
              className="booker-profile-btn booker-profile-btn--danger"
              onClick={onRemove}
              disabled={saving || removing}
            >
              <FiTrash2 aria-hidden="true" />
              {removing ? t("Removing...") : t(removeLabel)}
            </button>
          ) : (
            <span className="booker-profile-footHint">{t(footHint)}</span>
          )}

          <div className="booker-profile-footActions">
            <button
              type="button"
              className="booker-profile-btn booker-profile-btn--secondary"
              onClick={closePopup}
              disabled={saving || removing}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="booker-profile-btn booker-profile-btn--primary"
              disabled={saving || removing || submitDisabled}
            >
              <FiSave aria-hidden="true" />
              {saving ? t("Saving...") : mode === "edit" ? t("Save changes") : t(createLabel)}
            </button>
          </div>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default GuestProfileDialog;
